import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe2, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSiteContent } from '../contexts/SiteContentContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const defaultLinks = [
  { path: '/', label: 'Əsas Səhifə' },
  { path: '/about', label: 'Haqqımızda' },
  { path: '/services', label: 'Xidmətlər' },
  { path: '/news', label: 'Yeniliklər' },
  { path: '/contact', label: 'Əlaqə' },
];

const typeIcons = { news: '📰', page: '📄', category: '🌍' };

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [c, setC] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();

  const siteContent = useSiteContent();
  useEffect(() => { if (siteContent) setC(siteContent.header_settings || {}); }, [siteContent]);
  useEffect(() => { const fn = () => setScrolled(window.scrollY > 20); window.addEventListener('scroll', fn); return () => window.removeEventListener('scroll', fn); }, []);
  useEffect(() => { setMobileOpen(false); setSearchOpen(false); setSearchQuery(''); }, [location]);

  // Close search on outside click
  useEffect(() => {
    const fn = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) { setSearchOpen(false); } };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  // Search debounce
  useEffect(() => {
    if (searchQuery.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    const timer = setTimeout(() => {
      axios.get(`${API}/api/search?q=${encodeURIComponent(searchQuery)}`).then(r => setSearchResults(r.data)).catch(() => setSearchResults([])).finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Focus input when search opens
  useEffect(() => { if (searchOpen && inputRef.current) inputRef.current.focus(); }, [searchOpen]);

  const siteName = c?.site_name || 'Dərsimiz Coğrafiya';
  const siteLogo = c?.site_logo || '';
  // Nav links from menu_config (Menyu İdarəsi controls public navbar)
  const menuConfig = siteContent?.menu_config;
  const pagePathMap = { homepage: '/', aboutpage: '/about', servicespage: '/services', newspage: '/news', contactpage: '/contact' };
  const navLinks = menuConfig?.items
    ? menuConfig.items
        .filter(m => m.active !== false && !['headerpage','footerpage'].includes(m.id))
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(m => ({ path: m.isCustom ? `/${m.slug || m.id}` : (pagePathMap[m.id] || '/'), label: m.label }))
    : defaultLinks;
  const ctaText = c?.cta_text || 'Bizimlə Əlaqə';
  const ctaUrl = c?.cta_url || '/contact';
  const loginText = c?.login_text || 'Daxil ol';
  const loginUrl = c?.login_url || '/student';
  const buttonOrder = c?.button_order || 'cta_first';

  const ctaBtn = <Link to={ctaUrl} data-testid="nav-contact-cta" className="px-5 py-2.5 rounded-full text-sm font-semibold bg-[#0077B6] text-white hover:bg-[#005f92] transition-all duration-300 shadow-md hover:shadow-lg">{ctaText}</Link>;
  const loginBtn = <a href={loginUrl} target="_blank" rel="noopener noreferrer" data-testid="nav-student-login" className="px-5 py-2.5 rounded-full text-sm font-semibold bg-[#7B2D8E] text-white hover:bg-[#6A1B7A] transition-all duration-300 shadow-md hover:shadow-lg">{loginText}</a>;

  return (
    <header data-testid="main-navbar" className={`sticky top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'glass-header shadow-md' : 'bg-white border-b border-[#E2E8F0]'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-[70px]">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
            {siteLogo ? (
              <img src={siteLogo} alt={siteName} className="h-8 w-auto object-contain group-hover:scale-105 transition-transform duration-300" />
            ) : (
              <div className="earth-globe-wrapper">
                <div className="earth-globe-tilt">
                  <div className="earth-globe-sphere w-9 h-9" style={{backgroundImage: `url(${API}/api/static/uploads/world_map.jpg)`}} />
                </div>
              </div>
            )}
            <span className="font-bold text-lg text-[#03045E] leading-tight">{siteName}</span>
          </Link>
          <nav className="hidden lg:flex items-center">
            <div className="flex items-center bg-[#F4F7F6] rounded-full p-1">
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path} data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                  className="relative px-4 py-2 text-sm font-medium transition-all duration-300">
                  {location.pathname === link.path && <motion.div layoutId="navBubble" className="absolute inset-0 bg-white rounded-full shadow-sm" transition={{ type: "spring", bounce: 0.2, duration: 0.6 }} />}
                  <span className={`relative z-10 ${location.pathname === link.path ? 'text-[#0077B6] font-semibold' : 'text-[#5C6B7A] hover:text-[#1A2530]'}`}>{link.label}</span>
                  {location.pathname === link.path && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-[#0077B6] rounded-full" />}
                </Link>
              ))}
            </div>
          </nav>
          <div className="hidden lg:flex items-center gap-2" ref={searchRef}>
            {/* Search */}
            <div className="relative">
              <button onClick={() => { setSearchOpen(!searchOpen); if (!searchOpen) setSearchQuery(''); }}
                data-testid="search-toggle"
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${searchOpen ? 'bg-[#0077B6] text-white shadow-md' : 'bg-[#F4F7F6] text-[#5C6B7A] hover:bg-[#0077B6]/10 hover:text-[#0077B6]'}`}>
                <Search className="w-[18px] h-[18px]" />
              </button>

              <AnimatePresence>
                {searchOpen && (
                  <motion.div initial={{ opacity: 0, y: 8, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.95 }}
                    className="absolute right-0 top-14 w-[420px] bg-white rounded-2xl shadow-2xl border border-[#E2E8F0] overflow-hidden z-50">
                    {/* Search Input */}
                    <div className="p-4 border-b border-[#E2E8F0]">
                      <div className="relative">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6B7A]" />
                        <input ref={inputRef} type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                          placeholder="Saytda axtar..." data-testid="search-input"
                          className="w-full pl-10 pr-4 py-3 rounded-xl bg-[#F4F7F6] border-0 text-sm text-[#1A2530] placeholder:text-[#5C6B7A]/50 focus:outline-none focus:ring-2 focus:ring-[#0077B6]/20" />
                      </div>
                    </div>
                    {/* Results */}
                    <div className="max-h-[360px] overflow-y-auto">
                      {searching && (
                        <div className="p-6 text-center"><div className="w-6 h-6 border-2 border-[#0077B6] border-t-transparent rounded-full animate-spin mx-auto" /></div>
                      )}
                      {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                        <div className="p-6 text-center">
                          <Search className="w-10 h-10 text-[#E2E8F0] mx-auto mb-2" />
                          <p className="text-sm text-[#5C6B7A]">Nəticə tapılmadı</p>
                        </div>
                      )}
                      {!searching && searchResults.map((r, i) => (
                        <Link key={i} to={r.url} onClick={() => setSearchOpen(false)}
                          className="flex items-start gap-3 p-4 hover:bg-[#F4F7F6] transition-colors border-b border-[#E2E8F0] last:border-0">
                          <span className="text-lg mt-0.5">{typeIcons[r.type] || '📄'}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-[#1A2530] truncate">{r.title}</div>
                            {r.summary && <p className="text-xs text-[#5C6B7A] mt-0.5 line-clamp-2">{r.summary}</p>}
                          </div>
                          <span className="text-[10px] text-[#0077B6] font-semibold uppercase bg-[#0077B6]/10 px-2 py-0.5 rounded-full flex-shrink-0">
                            {r.type === 'news' ? 'Xəbər' : r.type === 'category' ? 'Kateqoriya' : 'Səhifə'}
                          </span>
                        </Link>
                      ))}
                      {!searching && searchQuery.length < 2 && (
                        <div className="p-6 text-center">
                          <p className="text-xs text-[#5C6B7A]">Axtarış üçün ən azı 2 simvol daxil edin</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {buttonOrder === 'login_first' ? <>{loginBtn}{ctaBtn}</> : <>{ctaBtn}{loginBtn}</>}
          </div>
          <button data-testid="mobile-menu-toggle" className="lg:hidden p-2 rounded-lg hover:bg-[#0077B6]/10 transition-colors" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-white border-t border-[#E2E8F0] overflow-hidden">
            <nav className="flex flex-col p-4 gap-1">
              {/* Mobile Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6B7A]" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Saytda axtar..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#F4F7F6] text-sm focus:outline-none focus:ring-2 focus:ring-[#0077B6]/20" />
              </div>
              {searchQuery.length >= 2 && searchResults.length > 0 && (
                <div className="mb-3 bg-[#F4F7F6] rounded-xl overflow-hidden">
                  {searchResults.slice(0,5).map((r, i) => (
                    <Link key={i} to={r.url} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 hover:bg-white transition-colors border-b border-[#E2E8F0] last:border-0">
                      <span>{typeIcons[r.type]||'📄'}</span>
                      <span className="text-sm text-[#1A2530] truncate">{r.title}</span>
                    </Link>
                  ))}
                </div>
              )}
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path} className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${location.pathname === link.path ? 'bg-[#0077B6]/10 text-[#0077B6] font-semibold' : 'text-[#1A2530] hover:bg-[#F4F7F6]'}`}>{link.label}</Link>
              ))}
              <div className="mt-2 space-y-2">
                {buttonOrder === 'login_first' ? (
                  <><a href={loginUrl} target="_blank" rel="noopener noreferrer" className="block px-4 py-3 rounded-xl text-sm font-semibold bg-[#7B2D8E] text-white text-center">{loginText}</a>
                  <Link to={ctaUrl} className="block px-4 py-3 rounded-xl text-sm font-semibold bg-[#0077B6] text-white text-center">{ctaText}</Link></>
                ) : (
                  <><Link to={ctaUrl} className="block px-4 py-3 rounded-xl text-sm font-semibold bg-[#0077B6] text-white text-center">{ctaText}</Link>
                  <a href={loginUrl} target="_blank" rel="noopener noreferrer" className="block px-4 py-3 rounded-xl text-sm font-semibold bg-[#7B2D8E] text-white text-center">{loginText}</a></>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
